export const DEFAULT_APP_HANDOFF_TIMEOUT_MS = 8000;

export type AppHandoffStatus = 'idle' | 'opening' | 'opened' | 'failed';

export type AppHandoffWatchOptions = {
  timeoutMs?: number;
  onOpened: () => void;
  onFailed: () => void;
};

/** Detects likely app open via visibility; otherwise fails after timeout. */
export function watchAppHandoff(options: AppHandoffWatchOptions): () => void {
  const timeoutMs = options.timeoutMs ?? DEFAULT_APP_HANDOFF_TIMEOUT_MS;
  let settled = false;

  const cleanupFns: Array<() => void> = [];

  const cleanup = (): void => {
    for (const fn of cleanupFns) {
      fn();
    }
    cleanupFns.length = 0;
  };

  const settle = (result: 'opened' | 'failed'): void => {
    if (settled) {
      return;
    }
    settled = true;
    cleanup();
    if (result === 'opened') {
      options.onOpened();
    } else {
      options.onFailed();
    }
  };

  const onVisibilityChange = (): void => {
    if (document.hidden) {
      settle('opened');
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  cleanupFns.push(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  const timer = setTimeout(() => {
    if (document.hidden) {
      settle('opened');
    } else {
      settle('failed');
    }
  }, timeoutMs);
  cleanupFns.push(() => {
    clearTimeout(timer);
  });

  return cleanup;
}
